'use client';

import {
  forwardRef,
  useCallback,
  useMemo,
  useState,
  type HTMLAttributes,
} from 'react';

import { cn } from '../../../../lib/utils';
import {
  ToggleGroupContext,
  type ToggleGroupContextValue,
} from './ToggleGroupContext';

export type ToggleGroupType = 'single' | 'multi';

export interface ToggleGroupProps extends Omit<
  HTMLAttributes<HTMLDivElement>,
  'defaultValue'
> {
  /** 선택 모드 */
  type: ToggleGroupType;
  /** Controlled value (single: string | null, multi: string[]) */
  value?: string | string[] | null;
  /** Uncontrolled 초기값 */
  defaultValue?: string | string[] | null;
  /** 값 변경 콜백 */
  onValueChange?: (value: string | string[] | null) => void;
  /** 전체 비활성화 */
  disabled?: boolean;
  children: React.ReactNode;
}

/**
 * ToggleGroup - 토글 아이템 그룹 컨테이너
 *
 * ToggleItem들을 감싸며, Context를 통해 선택 상태를 관리합니다.
 * single 모드에서는 하나만, multi 모드에서는 복수 선택이 가능합니다.
 */
const ToggleGroup = forwardRef<HTMLDivElement, ToggleGroupProps>(
  (
    {
      type,
      value: controlledValue,
      defaultValue,
      onValueChange,
      disabled = false,
      children,
      className,
      ...props
    },
    ref,
  ) => {
    const isControlled = controlledValue !== undefined;
    const initValue = defaultValue ?? (type === 'multi' ? [] : null);
    const [internalValue, setInternalValue] = useState<
      string | string[] | null
    >(initValue);

    const value = isControlled ? controlledValue : internalValue;

    const handleToggle = useCallback(
      (itemValue: string) => {
        if (disabled) return;

        let next: string | string[] | null;

        if (type === 'single') {
          next = value === itemValue ? null : itemValue;
        } else {
          const current = Array.isArray(value) ? value : [];
          next = current.includes(itemValue)
            ? current.filter((v) => v !== itemValue)
            : [...current, itemValue];
        }

        if (!isControlled) setInternalValue(next);
        onValueChange?.(next);
      },
      [type, value, disabled, isControlled, onValueChange],
    );

    const isPressed = useCallback(
      (itemValue: string): boolean => {
        if (type === 'single') return value === itemValue;
        return Array.isArray(value) && value.includes(itemValue);
      },
      [type, value],
    );

    const ctx = useMemo<ToggleGroupContextValue>(
      () => ({ type, disabled, isPressed, onToggle: handleToggle }),
      [type, disabled, isPressed, handleToggle],
    );

    return (
      <ToggleGroupContext.Provider value={ctx}>
        <div
          ref={ref}
          role="group"
          aria-disabled={disabled || undefined}
          className={cn(
            'flex items-center gap-4xs rounded-2xs bg-sys-style-neutral-lightest p-4xs',
            className,
          )}
          {...props}
        >
          {children}
        </div>
      </ToggleGroupContext.Provider>
    );
  },
);

ToggleGroup.displayName = 'ToggleGroup';

export default ToggleGroup;
